import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bot, X, Send, Loader2, Plus, History, LifeBuoy, Star, BookOpen, Settings, Play } from "lucide-react";
import { ETIQUETA_TUTORIAL, abrirTutorial, sinEtiqueta, tutorialDe } from "@/lib/tutoriales";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { ABRIR_ASISTENTE } from "@/lib/asistente";
import { toast } from "sonner";
import { AccesosRapidos } from "@/components/asistente/AccesosRapidos";
import { RESPUESTAS } from "@/components/asistente/respuestasRapidas";
import { BotonMicrofono } from "@/components/asistente/BotonMicrofono";
import { useVozAsistente, idiomaVoz } from "@/components/asistente/useVozAsistente";

const BIENVENIDA = { id: "hola", remitente: "asistente", contenido: "¡Hola! Soy el asistente de NuvliPro. Pregúntame sobre planes, presupuestos, facturas, gastos, fichaje… y te ayudo paso a paso." };
const BIENVENIDA_PUBLICA = { id: "hola", remitente: "asistente", contenido: "¡Hola! Soy el asistente de NuvliPro. Pregúntame por los planes, los precios o qué puedes hacer con la app antes de crear tu cuenta." };

function Texto({ t }) {
  return t.split("\n").map((l, i) => (
    <p key={i} className={l.trim() ? "" : "h-2"}>
      {l.split(/(\*\*[^*]+\*\*)/g).map((s, j) => (s.startsWith("**") && s.endsWith("**") ? <b key={j}>{s.slice(2, -2)}</b> : s))}
    </p>
  ));
}

// Si pide ver un tutorial, se abre solo al terminar la respuesta; si no, queda el botón «Ver tutorial».
const PIDE_TUTORIAL = /tutorial|ens[eé]ñame|mu[eé]strame|ver c[oó]mo/i;

function BotonTutorial({ contenido, onAbrir }) {
  const tut = tutorialDe(contenido?.match(ETIQUETA_TUTORIAL)?.[1]);
  if (!tut) return null;
  return (
    <button onClick={() => { onAbrir?.(); abrirTutorial(tut.id); }} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#00B4CC] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#009DB3]" data-testid={`asistente-ver-tutorial-${tut.id}`}>
      <Play className="h-3 w-3 fill-current" /> Ver tutorial: {tut.titulo}
    </button>
  );
}

function Burbuja({ m, resaltado, onTutorial }) {
  const yo = m.remitente === "usuario";
  return (
    <div className={`flex ${yo ? "justify-end" : "justify-start"} animate-in fade-in-0 slide-in-from-bottom-1 duration-200`} data-testid={`asistente-msg-${m.remitente}`} data-msg-id={m.id}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed transition-shadow duration-300 ${yo ? "bg-[#1B2B4B] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"} ${resaltado ? "ring-2 ring-[#00B4CC]/70 shadow-md" : ""}`} data-resaltado={resaltado ? "true" : undefined}>
        <Texto t={(yo ? m.contenido : sinEtiqueta(m.contenido || "")) || "…"} />
        {!yo && <BotonTutorial contenido={m.contenido} onAbrir={onTutorial} />}
        {!!m.articulos?.length && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid="asistente-fuentes">
            {m.articulos.map((a) => <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-[#00B4CC]/10 px-2 py-0.5 text-[11px] text-[#007A8A]"><BookOpen className="w-3 h-3" />{a.titulo}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

const ITEM = "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-slate-50 transition-colors";

function Soporte({ onContacto, publico }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="asistente-escalado">
      <p className={publico ? "mb-2" : ""}>{publico ? "Para esta consulta es mejor que hables con una persona del equipo de NuvliPro." : "Hemos pasado tu consulta al equipo de soporte de NuvliPro. Te responderemos lo antes posible."}</p>
      {publico && (
        <div className="flex flex-wrap gap-2">
          <button onClick={onContacto} className="inline-flex items-center gap-1.5 rounded-full bg-[#1B2B4B] px-3 py-1.5 font-semibold text-white hover:bg-[#13203A] transition-colors" data-testid="asistente-abrir-contacto"><LifeBuoy className="w-3.5 h-3.5" /> Escribir al equipo</button>
          <Link to="/login?mode=register" className="inline-flex items-center rounded-full border border-[#1B2B4B] px-3 py-1.5 font-semibold text-[#1B2B4B] hover:bg-white transition-colors" data-testid="asistente-crear-cuenta">Crear cuenta gratis</Link>
        </div>
      )}
    </div>
  );
}

function MenuAjustes({ publico, onHistorial, onPersona, onCerrar }) {
  return (
    <>
      <div className="absolute inset-0 z-10" onClick={onCerrar} data-testid="asistente-menu-fondo" />
      <div className="absolute right-3 top-12 z-20 w-60 rounded-xl border border-slate-200 bg-white p-1 text-sm text-slate-700 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150" data-testid="asistente-menu">
        {!publico && <button onClick={onHistorial} className={ITEM} data-testid="asistente-menu-historial"><History className="w-4 h-4 text-[#00B4CC]" /> Historial de conversaciones</button>}
        <button onClick={onPersona} className={ITEM} data-testid="asistente-menu-persona"><LifeBuoy className="w-4 h-4 text-[#00B4CC]" /> Hablar con una persona</button>
      </div>
    </>
  );
}

function Cabecera({ onNueva, onAjustes, onCerrar }) {
  return (
    <div className="flex items-center gap-2 bg-[#1B2B4B] px-4 py-3 text-white">
      <Bot className="w-5 h-5 text-[#00B4CC]" />
      <div className="flex-1 min-w-0"><p className="font-heading font-bold text-sm">Asistente NuvliPro</p><p className="text-[11px] text-white/60">Respuestas con IA · puede equivocarse</p></div>
      <button onClick={onNueva} title="Nueva conversación" className="p-1.5 rounded-lg hover:bg-white/10" data-testid="asistente-nueva"><Plus className="w-4 h-4" /></button>
      <button onClick={onAjustes} title="Ajustes" className="p-1.5 rounded-lg hover:bg-white/10" data-testid="asistente-ajustes"><Settings className="w-4 h-4" /></button>
      <button onClick={onCerrar} title="Cerrar" className="p-1.5 rounded-lg hover:bg-white/10" data-testid="asistente-cerrar"><X className="w-4 h-4" /></button>
    </div>
  );
}

function Valorar({ conv, onHecho }) {
  const [nota, setNota] = useState(conv?.satisfaccion || 0);
  const enviar = async (n) => {
    setNota(n);
    try { await api.post(`/asistente/conversaciones/${conv.id}/calificar`, { puntuacion: n }); toast.success("¡Gracias por tu valoración!"); onHecho(n); } catch { /* sin aviso */ }
  };
  return (
    <div className="flex items-center justify-center gap-1 text-xs text-slate-500" data-testid="asistente-valorar">
      ¿Te ha ayudado?
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => enviar(n)} className="p-0.5 transition-transform hover:scale-110" data-testid={`asistente-estrella-${n}`} aria-label={`${n} estrellas`}>
          <Star className={`w-4 h-4 ${n <= nota ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

async function leerStream(res, onDelta) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", fin = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const partes = buf.split("\n\n");
    buf = partes.pop();
    partes.forEach((p) => {
      if (!p.startsWith("data: ")) return;
      const d = JSON.parse(p.slice(6));
      if (d.delta) onDelta(d.delta);
      if (d.fin) fin = d.mensaje;
    });
  }
  return fin;
}

export function AsistenteWidget({ publico = false }) {
  const [abierto, setAbierto] = useState(false);
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [lista, setLista] = useState(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [escPublico, setEscPublico] = useState(false);
  const [menu, setMenu] = useState(false);
  const [resaltado, setResaltado] = useState(null);
  const fondo = useRef(null);
  useEffect(() => { fondo.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    const abrir = () => setAbierto(true);
    window.addEventListener(ABRIR_ASISTENTE, abrir);
    return () => window.removeEventListener(ABRIR_ASISTENTE, abrir);
  }, []);
  const escalado = publico ? escPublico : conv?.requiere_escalacion;

  const nueva = () => { setConv(null); setMsgs([]); setLista(null); setEscPublico(false); setMenu(false); };
  const verHistorial = async () => { setMenu(false); const r = await api.get("/asistente/conversaciones").catch(() => ({ data: [] })); setLista(r.data); };
  const abrirConv = async (id) => { const r = await api.get(`/asistente/conversaciones/${id}`); setConv(r.data); setMsgs(r.data.mensajes); setLista(null); };

  const enviar = async (dicho) => {
    const t = (typeof dicho === "string" ? dicho : texto).trim();
    if (!t || enviando) return;
    setEnviando(true); setTexto("");
    try {
      const c = publico ? null : conv || (await api.post("/asistente/conversaciones")).data;
      if (c) setConv(c);
      const historial = msgs.filter((m) => m.contenido).slice(-6).map(({ remitente, contenido }) => ({ remitente, contenido }));
      setMsgs((m) => [...m, { id: `u-${Date.now()}`, remitente: "usuario", contenido: t }, { id: "stream", remitente: "asistente", contenido: "" }]);
      const res = await fetch(publico ? `${API}/asistente/publico/mensaje` : `${API}/asistente/conversaciones/${c.id}/mensajes`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(publico ? { texto: t, historial } : { texto: t }),
      });
      if (!res.ok) throw new Error(formatApiErrorDetail((await res.json().catch(() => ({}))).detail));
      const fin = await leerStream(res, (d) => setMsgs((m) => m.map((x) => (x.id === "stream" ? { ...x, contenido: x.contenido + d } : x))));
      if (fin) {
        setMsgs((m) => m.map((x) => (x.id === "stream" ? fin : x)));
        const tid = fin.contenido?.match(ETIQUETA_TUTORIAL)?.[1];
        if (!publico && tid && PIDE_TUTORIAL.test(t)) { setAbierto(false); abrirTutorial(tid); }
        if (fin.escalar && publico) setEscPublico(true);
        else if (fin.escalar) setConv((v) => ({ ...v, requiere_escalacion: true }));
      }
    } catch (e) {
      setMsgs((m) => m.filter((x) => x.id !== "stream"));
      toast.error(e.message || "No se pudo enviar el mensaje");
    } finally { setEnviando(false); }
  };

  const contacto = () => {
    setAbierto(false);
    const el = document.getElementById("contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" }); else window.location.assign("/#contacto");
  };
  const persona = async () => {
    setMenu(false);
    if (publico) return contacto();
    if (!conv) return toast.info("Escribe primero tu consulta y después pulsa «Hablar con una persona»: se la pasaremos al equipo de soporte.");
    if (!conv.requiere_escalacion) await api.post(`/asistente/conversaciones/${conv.id}/escalar`).catch(() => {});
    setConv({ ...conv, requiere_escalacion: true });
    toast.success("Hemos pasado tu consulta al equipo de soporte. Te responderemos lo antes posible.");
  };
  const rapido = async (id, label) => {
    setMenu(false);
    let respuesta = RESPUESTAS[id];
    if (id === "soporte" && publico) setEscPublico(true);
    else if (id === "soporte" && conv) {
      if (!conv.requiere_escalacion) await api.post(`/asistente/conversaciones/${conv.id}/escalar`).catch(() => {});
      setConv((v) => ({ ...v, requiere_escalacion: true }));
    } else if (id === "soporte") respuesta = RESPUESTAS.soporteSinConsulta;
    const previa = msgs.find((m) => m.remitente === "asistente" && m.contenido === respuesta);
    if (previa) {
      document.querySelector(`[data-msg-id="${previa.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setResaltado(previa.id);
      setTimeout(() => setResaltado((r) => (r === previa.id ? null : r)), 1500);
      return;
    }
    const n = Date.now();
    setMsgs((m) => [...m, { id: `qu-${n}`, remitente: "usuario", contenido: label }, { id: `qa-${n}`, remitente: "asistente", contenido: respuesta }]);
  };
  const { i18n } = useTranslation();
  const enNuvli = useLocation().pathname.startsWith("/nuvli");
  const voz = useVozAsistente({ lang: idiomaVoz(i18n.language), onTexto: setTexto, onFin: (t) => enviar(t) });

  if (!abierto) {
    return (
      <button onClick={() => setAbierto(true)} className={`fixed bottom-5 right-5 z-50 ${enNuvli ? "max-sm:hidden" : ""} flex min-h-8 items-center gap-1 whitespace-nowrap rounded-lg bg-[#1B2B4B] px-2.5 py-1.5 text-xs font-semibold leading-none text-white shadow-md shadow-[#1B2B4B]/30 hover:bg-[#13203A] hover:-translate-y-0.5 transition-[transform,background-color]`} aria-label="¿Te ayudo?" data-testid="asistente-launcher">
        <Bot className="h-4 w-4 shrink-0 text-[#00B4CC]" /> <span>¿Te ayudo?</span>
      </button>
    );
  }
  const respondidas = msgs.some((m) => m.remitente === "asistente" && m.id !== "stream");
  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-50 flex flex-col sm:w-[390px] sm:h-[600px] sm:max-h-[calc(100vh-40px)] bg-[#F7F9FC] sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in-0 slide-in-from-bottom-4 duration-300" data-testid="asistente-panel">
      <Cabecera onNueva={nueva} onAjustes={() => setMenu((v) => !v)} onCerrar={() => { setMenu(false); setAbierto(false); }} />
      {menu && <MenuAjustes publico={publico} onHistorial={verHistorial} onPersona={persona} onCerrar={() => setMenu(false)} />}
      {lista ? (
        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="asistente-lista">
          {lista.length === 0 && <p className="text-center text-sm text-slate-400 mt-8">Aún no tienes conversaciones.</p>}
          {lista.map((c) => (
            <button key={c.id} onClick={() => abrirConv(c.id)} className="w-full text-left rounded-xl border border-slate-200 bg-white px-3 py-2 hover:border-[#00B4CC] transition-colors" data-testid={`asistente-conv-${c.id}`}>
              <p className="text-sm font-medium text-slate-800 truncate">{c.tema || "Conversación"}</p>
              <p className="text-[11px] text-slate-400">{new Date(c.updated_at).toLocaleString("es-ES")}{c.requiere_escalacion ? " · enviada a soporte" : ""}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="asistente-mensajes">
          <Burbuja m={publico ? BIENVENIDA_PUBLICA : BIENVENIDA} />
          <AccesosRapidos onElegir={rapido} disabled={enviando} />
          {msgs.map((m) => <Burbuja key={m.id} m={m} resaltado={m.id === resaltado} onTutorial={() => setAbierto(false)} />)}
          {enviando && msgs.at(-1)?.contenido === "" && <Loader2 className="w-4 h-4 animate-spin text-slate-400" data-testid="asistente-pensando" />}
          {escalado && <Soporte onContacto={contacto} publico={publico} />}
          {!publico && respondidas && conv && <Valorar key={conv.id} conv={conv} onHecho={(n) => setConv((v) => ({ ...v, satisfaccion: n }))} />}
          <div ref={fondo} />
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); enviar(); }} className="flex items-end gap-2 border-t border-slate-200 bg-white p-3">
        <textarea value={texto} onChange={(e) => setTexto(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          rows={1} maxLength={2000} placeholder={voz.grabando ? "Escuchando…" : "Escribe tu pregunta…"} className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#00B4CC] max-h-28" data-testid="asistente-input" />
        <BotonMicrofono grabando={voz.grabando} disabled={enviando} onClick={() => voz.alternar(texto.trim() ? `${texto.trimEnd()} ` : "")} />
        <button type="submit" disabled={enviando || !texto.trim()} className="h-10 w-10 shrink-0 rounded-xl bg-[#00B4CC] text-white flex items-center justify-center hover:bg-[#009DB3] disabled:opacity-50 transition-colors" data-testid="asistente-enviar">
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
